import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import GradeLevelCard from "../components/gradeLevels/GradeLevelCard";
import CreateGradeLevelModal from "../components/gradeLevels/CreateGradeLevelModal";
import UpdateGradeLevelModal from "../components/gradeLevels/UpdateGradeLevelModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getGradeLevels,
  deleteGradeLevel,
  resetGradeLevelServerStatus,
} from "../redux/actions/gradeLevelActions";
import { GradeLevelResponseDto } from "../shared/dtos/gradeLevel/GradeLevelResponseDto";

const GradeLevelsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: gradeLevels,
    loadingFetchAll: loadingGradeLevels,
    errorFetchAll: errorLoadingGradeLevels,
    serverStatus,
    loadingCreate: loadingGradeLevelCreate,
    errorCreate: errorCreatingGradeLevel,
    loadingUpdate: loadingGradeLevelUpdate,
    errorUpdate: errorUpdatingGradeLevel,
    loadingDelete: loadingGradeLevelDelete,
  } = useAppSelector((state) => state.gradeLevel);

  // --- LOCAL MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedGrade, setSelectedGrade] =
    useState<GradeLevelResponseDto | null>(null);

  // --- PERMISSIONS ---
  const canCreate = hasPermission("grade_level:create");
  const canUpdate = hasPermission("grade_level:update");
  const canDelete = hasPermission("grade_level:delete");

  // --- FETCH DATA ---
  const fetchGrades = useCallback((): void => {
    dispatch(getGradeLevels());
  }, [dispatch]);

  useEffect(() => {
    fetchGrades();
  }, [fetchGrades]);

  // --- ORDER BY LEVEL ORDER ---
  const sortedGradeLevels = useMemo(() => {
    return [...gradeLevels].sort(
      (a, b) => (a.levelOrder ?? 0) - (b.levelOrder ?? 0),
    );
  }, [gradeLevels]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);
  const handleCloseCreate = useCallback((): void => {
    if (!loadingGradeLevelCreate) setIsCreateModalOpen(false);
  }, [loadingGradeLevelCreate]);

  const handleOpenUpdate = (grade: GradeLevelResponseDto): void => {
    setSelectedGrade(grade);
    setIsUpdateModalOpen(true);
  };
  const handleCloseUpdate = useCallback((): void => {
    if (!loadingGradeLevelUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedGrade(null);
    }
  }, [loadingGradeLevelUpdate]);

  const handleOpenDelete = (grade: GradeLevelResponseDto): void => {
    setSelectedGrade(grade);
    setIsDeleteModalOpen(true);
  };
  const handleCloseDelete = useCallback((): void => {
    if (!loadingGradeLevelDelete) {
      setIsDeleteModalOpen(false);
      setSelectedGrade(null);
    }
  }, [loadingGradeLevelDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedGrade) {
      dispatch(deleteGradeLevel(selectedGrade.id));
    }
  };

  // --- AUTO-CLOSE ON SERVER STATUS ---
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;
    if (operation === "create" && status === 201) handleCloseCreate();
    if (operation === "update" && status === 200) handleCloseUpdate();
    if (operation === "delete" && status === 200) handleCloseDelete();

    dispatch(resetGradeLevelServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Grade Levels"
      subtitle="Define educational levels and their hierarchy (e.g., Grade 1, Grade 2)."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Grade Level
          </AppButton>
        )
      }
    >
      <CardGridContainer
        loading={loadingGradeLevels}
        error={errorLoadingGradeLevels}
        itemCount={sortedGradeLevels.length}
        emptyMessage="No Grade Levels Found"
        onRetry={fetchGrades}
      >
        {sortedGradeLevels.map((grade) => (
          <GradeLevelCard
            key={grade.id}
            gradeLevel={grade}
            onEdit={canUpdate ? () => handleOpenUpdate(grade) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(grade) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateGradeLevelModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingGradeLevelCreate}
          error={errorCreatingGradeLevel}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedGrade && (
        <UpdateGradeLevelModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          gradeLevel={selectedGrade}
          loading={loadingGradeLevelUpdate}
          error={errorUpdatingGradeLevel}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedGrade && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Grade Level"
          description={`Are you sure you want to delete "${selectedGrade.name}"? This action cannot be undone.`}
          loading={loadingGradeLevelDelete}
        />
      )}
    </PageContainer>
  );
};

export default GradeLevelsPage;
